<!--
  Settings → Notifications, tab "Quote email links".

  Les deux liens (catalogue PDF, fiches techniques) ajoutés au mail d'envoi de
  devis, une ligne par langue client (table quotation_mail_link, migration 075).
  Demande de Ferdinand (2026-10-02) : à chaque mise à jour du catalogue sur Wix
  le nom du PDF change, il corrige le lien ici sans passer par le code.

  Page à part (Settings → Quote Email Links, six cartes empilées) jusqu'au
  2026-10-18 ; désormais un tableau des six langues d'un coup d'œil, et
  l'éditeur de la langue ouverte en dessous. Même API, mêmes contrôles.
  Interface en anglais, comme le reste de l'ERP.
-->
<template>
  <vx-card>
    <hv-section
      title="Quote email links"
      sub="Two links added to every quote sent from the Send Mail window, right after “You will find your quote #… attached.”, in the customer's language (from their contact record). A language with no link here gets the English links.">
      <template slot="actions">
        <vs-button size="small" type="border" icon-pack="feather" icon="icon-refresh-cw"
                   :disabled="loading" @click="load">Refresh</vs-button>
      </template>

      <hv-table :rows="rows" :loading="loading" :error="error" row-key="lang"
                empty-icon="LinkIcon" empty-text="No language configured.">
        <template slot="thead">
          <vs-th>Language</vs-th>
          <vs-th v-for="l in LINKS" :key="l.key">{{ l.title }}</vs-th>
          <vs-th>Last change</vs-th>
          <vs-th></vs-th>
        </template>
        <template slot="row" slot-scope="{ r }">
          <vs-td>
            <b>{{ langName(r.lang) }}</b><span class="hv2-muted ml-1">{{ r.lang }}</span>
            <div v-if="r.lang === FALLBACK_LANG" class="hv2-muted">also for any other language</div>
            <hv-pill v-if="isDirty(r)" tone="warn" label="Not saved" icon="EditIcon" class="mt-1" />
          </vs-td>
          <vs-td v-for="l in LINKS" :key="l.key">
            <template v-if="isHttp(r[l.key + '_url']) && r[l.key + '_label']">
              <a :href="r[l.key + '_url']" target="_blank" rel="noopener">{{ r[l.key + '_label'] }}</a>
              <div class="qel-url hv2-muted" :title="r[l.key + '_url']">{{ shortUrl(r[l.key + '_url']) }}</div>
            </template>
            <span v-else class="hv2-muted">not in the mail</span>
          </vs-td>
          <vs-td class="hv2-nowrap">
            {{ r.updated_at ? roWhen(r.updated_at) : DASH }}
            <div v-if="r.updated_by" class="hv2-muted">by {{ r.updated_by }}</div>
          </vs-td>
          <vs-td>
            <vs-button size="small" type="flat" icon-pack="feather" :icon="open === r.lang ? 'icon-chevron-up' : 'icon-edit-2'"
                       :title="open === r.lang ? 'Close' : 'Change these links'" @click="toggle(r)" />
          </vs-td>
        </template>
      </hv-table>

      <!-- The editor of the open language -->
      <div v-if="openRow" class="qel-editor">
        <div class="hv2-subhead">{{ langName(openRow.lang) }} <span class="hv2-muted">({{ openRow.lang }})</span></div>

        <div v-for="link in LINKS" :key="link.key" class="vx-row">
          <div class="vx-col md:w-1/3 w-full mb-4">
            <vs-input class="w-full" :label="link.title + ' — link text'"
                      v-model="openRow.form[link.key + '_label']"
                      :danger="!!labelError(openRow.form, link.key)"
                      :danger-text="labelError(openRow.form, link.key)" />
          </div>
          <div class="vx-col md:w-2/3 w-full mb-4">
            <div class="flex items-end">
              <vs-input class="w-full" :label="link.title + ' — URL'" :placeholder="link.placeholder"
                        v-model="openRow.form[link.key + '_url']"
                        :danger="!!urlError(openRow.form, link.key)"
                        :danger-text="urlError(openRow.form, link.key)" />
              <vs-button type="flat" icon-pack="feather" icon="icon-external-link" class="ml-2"
                         title="Open this link in a new tab"
                         :disabled="!isHttp(openRow.form[link.key + '_url'])"
                         @click="openUrl(openRow.form[link.key + '_url'])" />
            </div>
          </div>
        </div>

        <div class="qel-preview">
          <div class="hv2-muted mb-1">As it will appear in the mail</div>
          <!-- buildQuotationMailLinksHtml escapes the URL and the text. -->
          <div v-if="previewHtml(openRow.form)" v-html="previewHtml(openRow.form)"></div>
          <div v-else class="hv2-muted">No link: nothing is added to the mail for this language.</div>
        </div>

        <p class="hv2-sec-sub mt-3 mb-3">
          When the catalogue is updated on Wix, the PDF file name changes: paste the new link here and check it with
          the <feather-icon icon="ExternalLinkIcon" svgClasses="w-3 h-3" /> button. Leave a URL empty to leave that link out of the mail.
        </p>

        <div class="sumal-settings-actions">
          <vs-button size="small" :disabled="!isDirty(openRow) || !isValid(openRow.form) || openRow.saving" @click="save(openRow)">
            {{ openRow.saving ? 'Saving…' : 'Save' }}
          </vs-button>
          <vs-button size="small" type="border" class="ml-2" :disabled="!isDirty(openRow) || openRow.saving" @click="reset(openRow)">
            Cancel the changes
          </vs-button>
        </div>
      </div>
    </hv-section>
  </vx-card>
</template>

<script>
import axios from '@/axios.js'
import HvSection from '@/views/harvesting2/components/HvSection'
import HvTable from '@/views/harvesting2/components/HvTable'
import HvPill from '@/views/harvesting2/components/HvPill'
import formatMixin from '@/views/harvesting2/formatMixin'
import { buildQuotationMailLinksHtml } from '@/helpers/quotation-mail-links.js'
import { roWhen } from '@/helpers/romaniaTime'

const FIELDS = ['catalogue_url', 'catalogue_label', 'datasheet_url', 'datasheet_label']
// Ordre de la demande de Ferdinand.
const LANG_ORDER = ['fr', 'en', 'nl', 'de', 'es', 'ro']
const LANG_NAMES = { fr: 'French', en: 'English', nl: 'Dutch', de: 'German', es: 'Spanish', ro: 'Romanian' }

function pickForm (row) {
  const form = {}
  FIELDS.forEach((f) => { form[f] = row[f] || '' })
  return form
}

export default {
  name: 'QuoteEmailLinks',
  components: { HvSection, HvTable, HvPill },
  mixins: [formatMixin],
  data () {
    return {
      FALLBACK_LANG: 'en',
      LINKS: [
        { key: 'catalogue', title: 'Catalogue (PDF)', placeholder: 'https://www.acaciabois.com/_files/ugd/….pdf' },
        { key: 'datasheet', title: 'Technical data sheets', placeholder: 'https://www.acaciabois.com/fiches-techniques' }
      ],
      rows: [],
      open: null,
      loading: true,
      error: ''
    }
  },
  computed: {
    openRow () { return this.rows.find((r) => r.lang === this.open) || null }
  },
  methods: {
    roWhen,
    langName (lang) { return LANG_NAMES[lang] || String(lang).toUpperCase() },
    isHttp (url) { return /^https?:\/\/\S+$/i.test(String(url || '').trim()) },
    // "acaciabois.com/_files/ugd/…catalogue-2026.pdf": the host and the end, the part that changes.
    shortUrl (url) {
      const s = String(url || '').trim().replace(/^https?:\/\/(www\.)?/i, '')
      return s.length > 48 ? `${s.slice(0, 18)}…${s.slice(-27)}` : s
    },
    urlError (form, key) {
      const url = String(form[`${key}_url`] || '').trim()
      return url && !this.isHttp(url) ? 'The URL must start with https://' : ''
    },
    labelError (form, key) {
      const url = String(form[`${key}_url`] || '').trim()
      const label = String(form[`${key}_label`] || '').trim()
      return url && !label ? 'Required when a URL is set' : ''
    },
    isValid (form) { return this.LINKS.every((l) => !this.urlError(form, l.key) && !this.labelError(form, l.key)) },
    isDirty (row) { return FIELDS.some((f) => String(row.form[f] || '').trim() !== (row[f] || '')) },
    previewHtml (form) {
      const trimmed = {}
      FIELDS.forEach((f) => { trimmed[f] = String(form[f] || '').trim() })
      return buildQuotationMailLinksHtml(trimmed).replace(/^<br\/><br\/>/, '')
    },
    openUrl (url) { window.open(String(url).trim(), '_blank', 'noopener') },
    // Closing a language keeps what was typed: its line says "Not saved".
    toggle (row) { this.open = this.open === row.lang ? null : row.lang },
    reset (row) { row.form = pickForm(row) },
    async load () {
      this.loading = true
      this.error = ''
      try {
        const { data } = await axios.get('/quotation-mail-links')
        const rank = (lang) => (LANG_ORDER.indexOf(lang) + 1) || 99
        this.rows = (data || [])
          .slice()
          .sort((a, b) => rank(a.lang) - rank(b.lang))
          .map((r) => ({ ...r, form: pickForm(r), saving: false }))
      } catch (e) {
        this.error = `The quote email links could not be read: ${(e.response && e.response.data && e.response.data.message) || e.message}`
      } finally {
        this.loading = false
      }
    },
    async save (row) {
      row.saving = true
      try {
        const payload = {}
        FIELDS.forEach((f) => { payload[f] = String(row.form[f] || '').trim() })
        const { data } = await axios.put(`/quotation-mail-links/${row.lang}`, payload)
        Object.assign(row, data)
        row.form = pickForm(row)
        this.$vs.notify({ color: 'success', title: 'Saved', text: `${this.langName(row.lang)} links will be used for the next quotes sent.`, position: 'bottom-right' })
      } catch (e) {
        this.$vs.notify({ color: 'danger', title: 'Not saved', text: (e.response && e.response.data && e.response.data.message) || e.message, time: 8000, position: 'bottom-right' })
      } finally {
        row.saving = false
      }
    }
  },
  created () { this.load() }
}
</script>
